import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/client/lib/api";
import { useAuth } from "@/client/lib/auth";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { CameraIcon, CheckIcon, UserIcon } from "lucide-react";

type ProfileData = {
  personId: number;
  givenName: string;
  familyName: string;
  familyNamePrefix: string | null;
  preferredName: string | null;
  hasAvatar: boolean;
};

// Downscale + center-crop any selected image to a small 256x256 JPEG before upload. Phone/camera
// photos are far larger than the 512 KB server cap, which previously made the upload silently fail;
// resizing here makes any photo work and keeps each avatar ~10-25 KB.
const AVATAR_SIZE = 256;
async function fileToAvatarBase64(file: File): Promise<{ contentType: string; dataBase64: string }> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("read_failed"));
    reader.readAsDataURL(file);
  });
  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("decode_failed"));
    i.src = dataUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no_canvas");
  // Cover: scale so the image fills the square, then center-crop.
  const scale = Math.max(AVATAR_SIZE / img.width, AVATAR_SIZE / img.height);
  const sw = AVATAR_SIZE / scale;
  const sh = AVATAR_SIZE / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
  const out = canvas.toDataURL("image/jpeg", 0.85);
  return { contentType: "image/jpeg", dataBase64: out.split(",")[1] ?? "" };
}

export function ProfilePage() {
  const { user, refresh: refreshAuth } = useAuth();
  const isAdmin = !!user?.roles.includes("admin");
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [familyNamePrefix, setFamilyNamePrefix] = useState("");
  const [preferredName, setPreferredName] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await (api.api as any).profile.$get();
      if (res.ok) {
        const data: ProfileData = await res.json();
        setProfile(data);
        setGivenName(data.givenName);
        setFamilyName(data.familyName);
        setFamilyNamePrefix(data.familyNamePrefix ?? "");
        setPreferredName(data.preferredName ?? "");
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!profile || !isAdmin) return;
    setSaving(true);
    setSaved(false);
    try {
      const res = await (api.api as any).profile.$put({
        json: {
          givenName: givenName.trim(),
          familyName: familyName.trim(),
          familyNamePrefix: familyNamePrefix.trim() || null,
          preferredName: preferredName.trim() || null,
        },
      });
      if (res.ok) {
        setSaved(true);
        await refreshAuth();
        setTimeout(() => setSaved(false), 2000);
      }
    } catch {}
    setSaving(false);
  }

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    setAvatarError(null);

    if (!file.type.startsWith("image/")) {
      setAvatarError("Kies een afbeelding (jpg, png of webp).");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploadingAvatar(true);
    try {
      const { contentType, dataBase64 } = await fileToAvatarBase64(file);
      const res = await (api.api as any).avatars[":personId"].$put({
        param: { personId: String(profile.personId) },
        json: { contentType, dataBase64 },
      });
      if (res.ok) {
        setAvatarVersion((v) => v + 1);
        setProfile((p) => (p ? { ...p, hasAvatar: true } : p));
        await refreshAuth();
      } else {
        setAvatarError("Het opslaan van de foto is niet gelukt. Probeer een andere afbeelding.");
      }
    } catch {
      setAvatarError("Deze afbeelding kon niet worden verwerkt. Probeer een andere.");
    }
    setUploadingAvatar(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleDeleteAvatar() {
    if (!profile) return;
    setAvatarError(null);
    setUploadingAvatar(true);
    try {
      const res = await (api.api as any).avatars[":personId"].$delete({
        param: { personId: String(profile.personId) },
      });
      if (res.ok) {
        setProfile((p) => (p ? { ...p, hasAvatar: false } : p));
        setAvatarVersion((v) => v + 1);
        await refreshAuth();
      } else {
        setAvatarError("Verwijderen is niet gelukt. Probeer het opnieuw.");
      }
    } catch {
      setAvatarError("Verwijderen is niet gelukt. Probeer het opnieuw.");
    }
    setUploadingAvatar(false);
  }

  if (loading) return <p className="text-muted-foreground">Laden...</p>;
  if (!profile) return <p className="text-muted-foreground">Profiel niet gevonden.</p>;

  const avatarUrl = profile.hasAvatar ? `/api/avatars/${profile.personId}?v=${avatarVersion}` : null;

  const hasChanges =
    givenName.trim() !== profile.givenName ||
    familyName.trim() !== profile.familyName ||
    (familyNamePrefix.trim() || null) !== (profile.familyNamePrefix ?? null) ||
    (preferredName.trim() || null) !== (profile.preferredName ?? null);

  return (
    <div className="space-y-6">
      <AdminPageHeader items={[{ label: "Profiel" }]} />

      {/* Avatar */}
      <div className="flex items-center gap-5">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploadingAvatar}
          className="group relative size-20 cursor-pointer overflow-hidden rounded-full [clip-path:circle(50%)]"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="size-20 rounded-full object-cover" />
          ) : (
            <span className="flex size-20 items-center justify-center rounded-full bg-muted">
              <UserIcon className="size-9 text-muted-foreground" />
            </span>
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <CameraIcon className="size-5 text-white" />
          </span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleAvatarUpload}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium">Profielfoto</p>
          <p className="text-xs text-muted-foreground">
            {uploadingAvatar ? "Bezig..." : "Klik op de foto om te wijzigen"}
          </p>
          <div className="mt-1.5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              className="cursor-pointer text-sm font-medium text-primary hover:underline disabled:opacity-50"
            >
              Foto kiezen
            </button>
            {profile.hasAvatar && (
              <button
                type="button"
                onClick={handleDeleteAvatar}
                disabled={uploadingAvatar}
                className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-destructive disabled:opacity-50"
              >
                Verwijderen
              </button>
            )}
          </div>
          {avatarError && <p className="mt-1 text-xs text-destructive">{avatarError}</p>}
        </div>
      </div>

      {/* Name form */}
      <form onSubmit={handleSave} className="max-w-md space-y-4">
        {!isAdmin && (
          <p className="text-sm text-muted-foreground">
            Je naam wordt beheerd door de beheerder. Je kunt hier wel je profielfoto aanpassen.
          </p>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="givenName">Voornaam</Label>
          <Input id="givenName" value={givenName} onChange={(e) => setGivenName(e.target.value)} disabled={!isAdmin} required />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="familyNamePrefix">Tussenvoegsel</Label>
          <Input
            id="familyNamePrefix"
            value={familyNamePrefix}
            onChange={(e) => setFamilyNamePrefix(e.target.value)}
            placeholder="bijv. van, de, van der"
            disabled={!isAdmin}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="familyName">Achternaam</Label>
          <Input id="familyName" value={familyName} onChange={(e) => setFamilyName(e.target.value)} disabled={!isAdmin} required />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="preferredName">Roepnaam</Label>
          <Input
            id="preferredName"
            value={preferredName}
            onChange={(e) => setPreferredName(e.target.value)}
            placeholder="Optioneel, als je liever anders aangesproken wordt"
            disabled={!isAdmin}
          />
        </div>

        {isAdmin && (
          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" disabled={saving || !hasChanges}>
              {saving ? (
                "Opslaan..."
              ) : saved ? (
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon className="size-4" /> Opgeslagen
                </span>
              ) : (
                "Opslaan"
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
